import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarPlus,
  ChevronLeft,
  ChevronRight,
  Mail,
  Phone,
  FileText,
  CheckCircle2,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/ui/page-header';
import { useMyOppActivites } from '@/hooks/queries/useCrmActivites';
import { useUser } from '@/hooks/useUser';
import { formatDateDZ, formatDateTimeDZ } from '@/lib/format';
import { cn } from '@/lib/utils';
import { t } from '@/locales/i18n';
import type { OppActiviteType, OppActiviteStatut } from '@/lib/database.types';

const ACTIVITE_ICON: Record<OppActiviteType, JSX.Element> = {
  note: <FileText className="h-3 w-3" />,
  appel: <Phone className="h-3 w-3" />,
  email: <Mail className="h-3 w-3" />,
  rdv: <CalendarPlus className="h-3 w-3" />,
  tache: <CheckCircle2 className="h-3 w-3" />,
  fichier: <FileText className="h-3 w-3" />,
};

type Vue = 'jour' | 'semaine' | 'mois';

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function startOfMonth(d: Date): Date {
  const x = startOfDay(d);
  x.setDate(1);
  return x;
}

function startOfWeek(d: Date): Date {
  const x = startOfDay(d);
  const day = (x.getDay() + 6) % 7; // Lundi = 0
  x.setDate(x.getDate() - day);
  return x;
}

export function CrmAgendaPage(): JSX.Element {
  const { user } = useUser();
  const [vue, setVue] = useState<Vue>('semaine');
  const [cursor, setCursor] = useState(() => startOfDay(new Date()));
  const [typeFilter, setTypeFilter] = useState<OppActiviteType | ''>('');

  const { from, to, days } = useMemo(() => {
    if (vue === 'jour') {
      const d = startOfDay(cursor);
      return {
        from: d,
        to: new Date(d.getTime() + 86400000),
        days: [d],
      };
    }
    if (vue === 'semaine') {
      const start = startOfWeek(cursor);
      const list = Array.from({ length: 7 }, (_, i) => new Date(start.getTime() + i * 86400000));
      return { from: start, to: new Date(start.getTime() + 7 * 86400000), days: list };
    }
    const start = startOfMonth(cursor);
    const end = new Date(start.getFullYear(), start.getMonth() + 1, 1);
    const list: Date[] = [];
    for (let d = new Date(start); d < end; d = new Date(d.getTime() + 86400000))
      list.push(new Date(d));
    return { from: start, to: end, days: list };
  }, [vue, cursor]);

  const { data: activites = [] } = useMyOppActivites({
    assigneeId: user?.id ?? null,
    dateFrom: from.toISOString(),
    dateTo: to.toISOString(),
  });

  const filtered = typeFilter ? activites.filter((a) => a.type === typeFilter) : activites;

  const byDay = useMemo(() => {
    const m = new Map<string, typeof activites>();
    for (const a of filtered) {
      if (!a.date_prevue) continue;
      const k = a.date_prevue.slice(0, 10);
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(a);
    }
    return m;
  }, [filtered]);

  const navigate = (delta: number): void => {
    const d = new Date(cursor);
    if (vue === 'jour') d.setDate(d.getDate() + delta);
    else if (vue === 'semaine') d.setDate(d.getDate() + 7 * delta);
    else d.setMonth(d.getMonth() + delta);
    setCursor(d);
  };

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        title="Mon agenda commercial"
        subtitle="Mes activités CRM (RDV, appels, tâches…) — vue jour / semaine / mois."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as OppActiviteType | '')}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="">— Tous types —</option>
              {(['rdv', 'appel', 'email', 'tache', 'note'] as OppActiviteType[]).map((t2) => (
                <option key={t2} value={t2}>
                  {t(`crm.activite.type.${t2}`)}
                </option>
              ))}
            </select>
            <div className="flex items-center gap-1 rounded-md border border-input bg-background p-0.5">
              {(['jour', 'semaine', 'mois'] as Vue[]).map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setVue(v)}
                  className={cn(
                    'rounded px-2 py-1 text-xs',
                    vue === v ? 'bg-primary text-primary-foreground' : 'text-muted-foreground',
                  )}
                >
                  {v}
                </button>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCursor(startOfDay(new Date()))}>
              Aujourd&apos;hui
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate(1)}>
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        }
      />

      <div className="text-xs text-muted-foreground">
        {vue === 'jour' && `Jour : ${formatDateDZ(cursor)}`}
        {vue === 'semaine' &&
          `Semaine du ${formatDateDZ(days[0]!)} au ${formatDateDZ(days[days.length - 1]!)}`}
        {vue === 'mois' && cursor.toLocaleDateString('fr-DZ', { month: 'long', year: 'numeric' })}
      </div>

      <div
        className={cn(
          'grid gap-2',
          vue === 'jour' ? 'grid-cols-1' : vue === 'semaine' ? 'grid-cols-7' : 'grid-cols-7',
        )}
      >
        {days.map((d) => {
          const key = d.toISOString().slice(0, 10);
          const dayActs = byDay.get(key) ?? [];
          const isToday = key === new Date().toISOString().slice(0, 10);
          return (
            <div
              key={key}
              className={cn(
                'min-h-[140px] rounded-md border bg-card p-2',
                isToday ? 'border-primary' : 'border-border',
              )}
            >
              <div className="mb-1 flex items-center justify-between text-[10px] uppercase tracking-wide text-muted-foreground">
                <span>
                  {d.toLocaleDateString('fr-DZ', { weekday: 'short' })} {d.getDate()}
                </span>
                {dayActs.length > 0 && <span>{dayActs.length}</span>}
              </div>
              <ul className="space-y-1">
                {dayActs.map((a) => (
                  <li
                    key={a.id}
                    className={cn(
                      'flex items-start gap-1 rounded border px-1.5 py-1 text-[11px]',
                      a.statut === 'termine'
                        ? 'border-success/40 bg-success/10 text-success'
                        : a.statut === 'a_faire'
                          ? 'border-primary/40 bg-primary/5'
                          : 'border-border bg-muted/40',
                    )}
                  >
                    <span className="mt-0.5">{ACTIVITE_ICON[a.type]}</span>
                    <Link
                      to={`/crm/opportunite/${a.opportunite_id}`}
                      className="line-clamp-1 flex-1 hover:underline"
                    >
                      {a.sujet ?? t(`crm.activite.type.${a.type}`)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      <section className="rounded-lg border border-border bg-card p-4">
        <h3 className="mb-3 text-sm font-semibold">Liste détaillée ({filtered.length})</h3>
        {filtered.length === 0 ? (
          <p className="text-xs text-muted-foreground">Aucune activité dans cet intervalle.</p>
        ) : (
          <ul className="space-y-2">
            {filtered.map((a) => (
              <li
                key={a.id}
                className="flex items-start gap-2 rounded-md border border-border/40 p-2 text-xs"
              >
                <span className="mt-0.5">{ACTIVITE_ICON[a.type]}</span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{t(`crm.activite.type.${a.type}`)}</Badge>
                    <Link
                      to={`/crm/opportunite/${a.opportunite_id}`}
                      className="font-medium hover:underline"
                    >
                      {a.sujet ?? '—'}
                    </Link>
                    <Badge
                      variant={
                        a.statut === 'termine'
                          ? 'success'
                          : (a.statut as OppActiviteStatut) === 'a_faire'
                            ? 'outline'
                            : 'secondary'
                      }
                    >
                      {t(`crm.activite.statut.${a.statut}`)}
                    </Badge>
                  </div>
                  {a.description && <div className="text-muted-foreground">{a.description}</div>}
                  {a.date_prevue && (
                    <div className="text-[10px] text-muted-foreground">
                      {formatDateTimeDZ(a.date_prevue)}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
